import { CommonModule } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { debounce } from 'lodash';
import { LiveFormsTranslateLoaderService } from '../../services/live-forms-translate-loader.service';

const DEFAULT_MENU_ITEM_BOTTOM_EDGE_OFFSET_PX = 64;

export interface MenuAction {
  action:
    | 'addComment'
    | 'addPhotoComment'
    | 'deleteItem'
    | 'getXml'
    | 'getFormattedXml'
    | 'setInputColor'
    | 'removeInputColor'
    | 'setSelectionColor'
    | 'removeSelectionColor';
  data?: any;
}

@Component({
  selector: 'lfe-item-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIcon, FormsModule],
  templateUrl: './item-menu.component.html',
  standalone: true,
  styleUrl: './item-menu.component.css'
})
export class ItemMenuComponent {
  readonly isVisible = input<boolean>(false);
  readonly canDeleteComment = input<boolean>(false);
  readonly itemInputColorControlEnabled = input.required<boolean>();
  readonly itemInputColor = input<string>('');
  readonly itemSelectionColorControlEnabled = input.required<boolean>();
  readonly itemSelectionColor = input<string>('');
  readonly debug = input<boolean>(false);

  readonly menuContainerRef = viewChild<ElementRef<HTMLDivElement>>('menuContainer');

  readonly menuAction = output<MenuAction>();
  readonly menuClose = output<void>();

  containerStyle = signal({});

  readonly colorPicker = viewChild<ElementRef>('colorPicker');
  readonly colorPickerTable = viewChild<ElementRef>('colorPickerTable');

  private readonly translateService = inject(LiveFormsTranslateLoaderService);

  itemInputColorLive = linkedSignal(() => this.itemInputColor());
  itemSelectionColorLive = linkedSignal(() => this.itemSelectionColor());

  isRemoveInputColorEnabled = computed(() => this.itemInputColorLive());

  showDeleteOption = computed(() => {
    return this.canDeleteComment();
  });

  // Translation getters
  get addCommentText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.add-comment');
  }

  get addPhotoCommentText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.add-photo-comment');
  }

  get setInputColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.set-input-color');
  }

  get removeInputColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.remove-input-color');
  }

  get setSelectionColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.set-fill-color');
  }

  get removeSelectionColor(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.clear-fill-color');
  }

  get deleteItemText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.actions.delete-item');
  }

  get getXmlText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.debug.get-xml');
  }

  get getFormattedXmlText(): string {
    return this.translateService.instant('liveforms-editor.item-menu.debug.get-formatted-xml');
  }

  _afterRenderEffect = afterRenderEffect(() => {
    this.setPositionToAvoidBottomEdge(this.isVisible());
  });

  setPositionToAvoidBottomEdge = (visible: boolean) => {
    this.containerStyle.set({});
    const el = this.menuContainerRef()?.nativeElement;

    if (!visible || !el) {
      return;
    }

    const { y, height } = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (height + y > viewportHeight - DEFAULT_MENU_ITEM_BOTTOM_EDGE_OFFSET_PX) {
      this.containerStyle.set({
        top: `calc(100% - ${Math.floor(height)}px)`
      });
    }
  };

  onAddComment(): void {
    this.menuAction.emit({ action: 'addComment' });
    this.menuClose.emit();
  }

  onAddPhotoComment(): void {
    this.menuAction.emit({ action: 'addPhotoComment' });
    this.menuClose.emit();
  }

  onOpenInputColorPicker(): void {
    this.colorPicker()?.nativeElement.focus();
    this.colorPicker()?.nativeElement.click();
  }

  debouncedSetInputColor = debounce((color: string) => {
    this.itemInputColorLive.set(color);
    this.menuAction.emit({ action: 'setInputColor', data: color });
  }, 300);

  onInputColorChange(event: Event): void {
    this.debouncedSetInputColor((event.target as HTMLInputElement).value);
  }

  onRemoveInputColor(): void {
    if (this.isRemoveInputColorEnabled()) {
      this.menuAction.emit({ action: 'removeInputColor' });
      this.itemInputColorLive.set('');
      this.menuClose.emit();
    }
  }

  onOpenTableColorPicker(): void {
    this.colorPickerTable()?.nativeElement.focus();
    this.colorPickerTable()?.nativeElement.click();
  }

  debouncedSetSelectionColor = debounce((color: string) => {
    this.itemSelectionColorLive.set(color);
    this.menuAction.emit({ action: 'setSelectionColor', data: color });
  }, 300);

  onSelectionColorChange(event: Event): void {
    this.debouncedSetSelectionColor((event.target as HTMLInputElement).value);
  }

  onRemoveSelectionColor(): void {
    this.menuAction.emit({ action: 'removeSelectionColor' });
    this.menuClose.emit();
  }

  onDeleteItem(): void {
    this.menuAction.emit({ action: 'deleteItem' });
  }

  onGetXml(): void {
    this.menuAction.emit({ action: 'getXml' });
    this.menuClose.emit();
  }

  onGetFormattedXml(): void {
    this.menuAction.emit({ action: 'getFormattedXml' });
    this.menuClose.emit();
  }

  onMenuClose(): void {
    this.containerStyle.set({});
    this.menuClose.emit();
  }
}
